import { useCallback, useEffect, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import useActionDuration from '../../../../../common/hooks/useActionDuration';
import { EVENTS } from '../../../../labelling-v2/atoms/label-store-dispatch/events';
import { useStageAtoms } from '../../../../labelling-v2/components/stage-scope/hooks';
import { ANNOTATION_TYPE } from '../../../constants';
import useStage from '../../../providers/hooks/use-stage';
import type { StagePointerEventT } from '../../../providers/stage-provider';
import type { RectDataT, SkeletonAnnotationT, SkeletonDataT, SkeletonTemplateT } from '../../../types';
import { isLabelingMode } from '../../toolbar/utils';
import { getDrawingRectCoords } from '../rectangle/utils';
import { SKELETON_TEMPLATE_BOUNDING_BOX_PADDING } from './constants';
import { fitSkeletonTemplateToBox } from './util';
type ParamsT = {
  isDrawing: boolean;
  templateConfig: SkeletonTemplateT;
};
export const useDrawSkeletonViaTemplate = ({
  isDrawing,
  templateConfig
}: ParamsT) => {
  const {
    stage,
    addAnnotation
  } = useStage();
  const {
    editorModeAtom,
    selectedAnnotationLabelAtom,
    dispatchAtom
  } = useStageAtoms();
  const editorMode = useAtomValue(editorModeAtom);
  const annotationLabel = useAtomValue(selectedAnnotationLabelAtom);
  const dispatch = useSetAtom(dispatchAtom);
  const {
    start,
    end
  } = useActionDuration();
  const [stampBox, setStampBox] = useState<RectDataT | null>(null);
  const [previewSkeleton, setPreviewSkeleton] = useState<SkeletonDataT | null>(null);
  const [initialPoint, setInitialPoint] = useState<[number, number] | null>(null);
  const onPointerDown = useCallback((stageEvent: StagePointerEventT) => {
    if (!annotationLabel) return;
    const {
      mousePos
    } = stageEvent.detail;
    setInitialPoint([mousePos[0], mousePos[1]]);
    start();
  }, [annotationLabel, start]);
  const onPointerMove = useCallback((stageEvent: StagePointerEventT) => {
    if (!initialPoint) return;
    const {
      mousePos
    } = stageEvent.detail;
    const box = getDrawingRectCoords(initialPoint[0], initialPoint[1], mousePos[0], mousePos[1]);
    setStampBox({
      ...box,
      rotationAngle: 0
    });
    const maxPadding = SKELETON_TEMPLATE_BOUNDING_BOX_PADDING;
    const paddingX = Math.min(maxPadding, box.width / 4);
    const paddingY = Math.min(maxPadding, box.height / 4);
    const fitBox = {
      x: box.x + paddingX,
      y: box.y + paddingY,
      width: box.width - 2 * paddingX,
      height: box.height - 2 * paddingY
    };
    if (fitBox.width > 0 && fitBox.height > 0) {
      setPreviewSkeleton(fitSkeletonTemplateToBox(templateConfig, fitBox));
    } else {
      setPreviewSkeleton(null);
    }
  }, [initialPoint, templateConfig]);
  const onPointerUp = useCallback(() => {
    if (!initialPoint || !annotationLabel || !previewSkeleton || !stampBox) {
      setInitialPoint(null);
      setStampBox(null);
      setPreviewSkeleton(null);
      return;
    }
    if (stampBox.width > 0 && stampBox.height > 0) {
      const annotation: SkeletonAnnotationT = {
        annotationType: ANNOTATION_TYPE.SKELETON,
        annotationLabel,
        data: previewSkeleton
      };
      const annotationId = addAnnotation(annotation, {
        actionDuration: end(),
        vertex: previewSkeleton.nodes.length
      });
      dispatch({
        type: EVENTS.SELECT_ANNOTATION,
        annotationId
      });
    }
    setInitialPoint(null);
    setStampBox(null);
    setPreviewSkeleton(null);
  }, [initialPoint, annotationLabel, previewSkeleton, stampBox, addAnnotation, dispatch, end]);
  useEffect(() => {
    if (!stage || !isLabelingMode(editorMode) || !isDrawing) return;
    stage.addEventListener('pointerdown', onPointerDown);
    stage.addEventListener('pointermove', onPointerMove);
    stage.addEventListener('pointerup', onPointerUp);
    return () => {
      stage.removeEventListener('pointerdown', onPointerDown);
      stage.removeEventListener('pointermove', onPointerMove);
      stage.removeEventListener('pointerup', onPointerUp);
    };
  }, [stage, editorMode, isDrawing, onPointerDown, onPointerMove, onPointerUp]);
  return {
    stampBox,
    previewSkeleton
  };
};